import type { GpSurfacesMapSamplerCoverageDepartment } from '../types/gp-surfaces-map';

import type { MergedDepartmentsGeoJson } from './gp-surfaces-geojson';

const SAMPLER_PALETTE = [
  '#228be6',
  '#40c057',
  '#fab005',
  '#fa5252',
  '#7950f2',
  '#15aabf',
  '#e64980',
  '#82c91e',
  '#fd7e14',
  '#4dabf7',
  '#12b886',
  '#be4bdb',
] as const;

export const INCOMPLETE_SAMPLER_COVERAGE_COLOR = '#e9ecef';

export type SamplerOrganizationColorMap = ReadonlyMap<number, string>;

/** One distinct palette slot per org in sorted id order (no id % 12 collisions). */
export function buildSamplerOrganizationColorMap(
  organizationIds: number[],
): SamplerOrganizationColorMap {
  const uniqueSorted = [...new Set(organizationIds)].sort((a, b) => a - b);
  const map = new Map<number, string>();
  uniqueSorted.forEach((id, index) => {
    map.set(id, SAMPLER_PALETTE[index % SAMPLER_PALETTE.length]);
  });
  return map;
}

export function colorMapFromOrganizations(
  organizations: { id: number; color?: string | null }[],
): SamplerOrganizationColorMap {
  const fromApi = new Map<number, string>();
  for (const organization of organizations) {
    if (organization.color) {
      fromApi.set(organization.id, organization.color);
    }
  }
  if (fromApi.size > 0) {
    return fromApi;
  }
  return buildSamplerOrganizationColorMap(
    organizations.map((organization) => organization.id),
  );
}

export function samplerOrganizationColor(
  organizationId: number,
  colorMap?: SamplerOrganizationColorMap,
): string {
  if (colorMap?.has(organizationId)) {
    return colorMap.get(organizationId)!;
  }
  return SAMPLER_PALETTE[Math.abs(organizationId) % SAMPLER_PALETTE.length];
}

export function fillColorForSamplerDepartment(
  department: GpSurfacesMapSamplerCoverageDepartment | undefined,
  colorMap: SamplerOrganizationColorMap,
): string {
  if (
    !department ||
    !department.is_complete ||
    department.dominant_sampler_organization_id == null
  ) {
    return INCOMPLETE_SAMPLER_COVERAGE_COLOR;
  }
  return samplerOrganizationColor(
    department.dominant_sampler_organization_id,
    colorMap,
  );
}

export function applySamplerCoverageFillColors(
  geojson: MergedDepartmentsGeoJson,
  departments: GpSurfacesMapSamplerCoverageDepartment[],
  colorMap: SamplerOrganizationColorMap,
): MergedDepartmentsGeoJson {
  const byCode = new Map(departments.map((department) => [department.code, department]));

  return {
    ...geojson,
    features: geojson.features.map((feature) => {
      const code = String(feature.properties?.code ?? '');
      const fillColor = fillColorForSamplerDepartment(byCode.get(code), colorMap);
      return {
        ...feature,
        properties: {
          ...feature.properties,
          fill_color: fillColor,
        },
      };
    }),
  };
}
